import { Section, WizardNew as Wizard } from "components/wizard";
import { PublishedStatusTypes, RouteNames } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useRtn } from "hooks/useRtn";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import { useCallback, useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { useNavigate } from "react-router";
import { useBackNavigation } from "utils/navigationUtils";
import Configure from "../Details/Configure";
import EmailTemplate from "../Details/EmailTemplate";
import LandingPage from "../Details/LandingPage";
import FeedbackPage from "../Details/FeedbackPage";
import { CampaignPublishV2Success } from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { campaignsStateSelector } from "../state/selectors";
import { Dimmer, Loader } from "semantic-ui-react";
import { bindAction } from "interfaces";
import { clearCampaign } from "../state/slices/campaignDetailsSlice";
import { reset } from "../state/slices/campaignValidationSlice";
import { getEmailTemplateContent } from "../state/thunks/emailTemplateThunk";
import { getLandingPageContent } from "../state/thunks/landingPageThunk";
import { clearLandingPage } from "../state/slices/landingPageSlice";
import { clearEmailTemplate } from "../state/slices/emailTemplateSlice";
import { clearFeedbackPage } from "../state/slices/feedbackPageSlice";
import { tryPublish } from "../state/thunks/campaignValidationThunk";
import PublishValidationInfo from "components/publishValidationInfo/PublishValidationInfo";
import { reset as resetOverview } from "../state/slices/campaignOverviewSlice";
import {
  resetAppliedFilter,
  resetPagination,
  resetSearch,
  resetShouldSort,
  resetSortingColumnName,
  resetSortingDirection,
} from "../state/slices/campaignFiltersSlice";
import { resetState } from "../state/slices/campaignEntityStateSlice";
import { resetTags } from "../state/slices/campaignTagsSlice";

const campaignsOverviewUrl = `/${RouteNames.contentSimulatedPhishingCampaigns}`;

export type Props = PropsFromRedux;

enum Steps {
  Configuration,
  EmailTemplate,
  LandingPage,
  FeedbackPage,
}

const pageIndexes = [Steps.Configuration, Steps.EmailTemplate, Steps.LandingPage, Steps.FeedbackPage];

const CreateCampaign = (props: Props) => {
  const navigate = useNavigate();
  const goBack = useBackNavigation(campaignsOverviewUrl);

  const [isValid, setIsValid] = useState(false);

  const {
    getEmailTemplateContent,
    getLandingPageContent,
    campaignValidation: { errorMode, emailTemplateValidation, landingPageValidation, feedbackPageValidation },
    resetSearchFilters,
  } = props;

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes(campaignsOverviewUrl)) resetSearchFilters();
    };
  }, [resetSearchFilters]);

  useEffect(
    () => {
      return () => {
        props.onUnmount();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (props.campaignId > 0) {
      getEmailTemplateContent(props.campaignId);
      getLandingPageContent(props.campaignId);
    }
  }, [props.campaignId, getEmailTemplateContent, getLandingPageContent]);

  const [wizardPages, { onNext, onPrevious, onFinish, goToPage }] = useWizardStepsManager(pageIndexes);

  const onPreviousAsync = async (activeStepIndex: number, nextIndex: number) => {
    onPrevious(activeStepIndex, nextIndex);
  };

  const handleOnFinish = async () => {
    onFinish?.();
    props.tryPublish(props.campaignId);
  };

  const getPublishedStatus = () => (props.isEntityCreated ? PublishedStatusTypes.notPublished : null);

  const hasActiveErrors =
    !emailTemplateValidation.resolved || !landingPageValidation.resolved || !feedbackPageValidation.resolved;

  const renderCustomHeader = (changeActiveIndex: (index: number) => void) => {
    const onErrorClick = () => {
      const validations = [
        { resolved: emailTemplateValidation.resolved, step: Steps.EmailTemplate },
        { resolved: landingPageValidation.resolved, step: Steps.LandingPage },
        { resolved: feedbackPageValidation.resolved, step: Steps.FeedbackPage },
      ];

      const unresolvedValidation = validations.find((validation) => !validation.resolved);

      if (unresolvedValidation) {
        changeActiveIndex(unresolvedValidation.step);
        goToPage(unresolvedValidation.step);
      }
    };

    return (
      errorMode && (
        <PublishValidationInfo
          unableToPublish={hasActiveErrors}
          readyToPublish={!hasActiveErrors}
          onErrorButtonClick={onErrorClick}
        />
      )
    );
  };

  const onPublished = useCallback(() => {
    navigate(campaignsOverviewUrl);
  }, [navigate]);

  useRtn(CampaignPublishV2Success, onPublished);

  return (
    <>
      <Dimmer active={props.isCreating} inverted>
        <Loader active={props.isCreating} />
      </Dimmer>
      <Wizard
        title="Create Phishing Campaign"
        finishButtonLabel="Publish"
        onCancel={goBack}
        onProgress={onNext}
        onRegressAsync={onPreviousAsync}
        onFinish={handleOnFinish}
        publishedStatus={getPublishedStatus()}
        isSaveInProgress={props.isSaving}
        isFinishButtonDisabled={hasActiveErrors || props.isUploadingFeedbackPageVideo}
        progressSavedDate={props.dateModified}
        renderCustomHeader={renderCustomHeader}
      >
        <Section label="Configuration" className="scrollable-content" isLocked={!isValid} required>
          <Configure acceptHandlers={wizardPages[Steps.Configuration]} onIsValidChange={setIsValid} />
        </Section>
        <Section label="Email" className="scrollable-content">
          <EmailTemplate acceptHandlers={wizardPages[Steps.EmailTemplate]} />
        </Section>
        <Section label="Landing Page" className="scrollable-content">
          <LandingPage acceptHandlers={wizardPages[Steps.LandingPage]} />
        </Section>
        <Section label="Feedback Page" className="scrollable-content">
          <FeedbackPage acceptHandlers={wizardPages[Steps.FeedbackPage]} />
        </Section>
      </Wizard>
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    campaignId: base.entityState.entityId,
    isEntityCreated: base.entityState.entityId > 0,
    isSaving: base.entityState.isEntityCommandInProgress,
    isCreating: base.entityState.changingEntityState,
    dateModified: base.entityState.lastModifiedDateTime,
    campaignValidation: base.campaignValidation,
    isUploadingFeedbackPageVideo: base.feedbackPage.isUploadingVideo,
  };
};
/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    tryPublish: bindAction(tryPublish, dispatch),
    getEmailTemplateContent: bindAction(getEmailTemplateContent, dispatch),
    getLandingPageContent: bindAction(getLandingPageContent, dispatch),
    onUnmount: () => {
      dispatch(clearCampaign());
      dispatch(clearEmailTemplate());
      dispatch(clearLandingPage());
      dispatch(clearFeedbackPage());
      dispatch(reset());
      dispatch(resetState());
      dispatch(resetTags());
    },
    resetSearchFilters: () => {
      dispatch(resetOverview());
      dispatch(resetSearch());
      dispatch(resetAppliedFilter());
      dispatch(resetPagination());
      dispatch(resetSortingColumnName());
      dispatch(resetSortingDirection());
      dispatch(resetShouldSort());
    },
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateCampaign);
export default ConnectedComponent;
